import React, { useState, useRef, createRef } from 'react';
import Button from '../../ui/Button';

const NUM_INPUTS = 4;

const PickupCodeInput = ({ onConfirm, isLoading = false, disabled = false }) => {
  const [codes, setCodes] = useState(Array(NUM_INPUTS).fill(''));
  const inputRefs = useRef(Array(NUM_INPUTS).fill(null).map(() => createRef()));

  const handleChange = (index, value) => {
    if (value.length > 1 && value.length === NUM_INPUTS && /^[0-9]+$/.test(value)) { // Handle paste
      const newCodes = value.split('').slice(0, NUM_INPUTS);
      setCodes(newCodes);
      inputRefs.current[NUM_INPUTS - 1]?.current?.focus();
      return;
    }

    if (/^[0-9]*$/.test(value) && value.length <= 1) { // Allow only single digit or empty
      const newCodes = [...codes];
      newCodes[index] = value;
      setCodes(newCodes);

      if (value && index < NUM_INPUTS - 1) {
        inputRefs.current[index + 1]?.current?.focus();
      }
    }
  };

  const handleKeyDown = (index, e) => {
    if (e.key === 'Backspace') {
      if (codes[index]) {
        const newCodes = [...codes];
        newCodes[index] = '';
        setCodes(newCodes);
        // Focus previous input if current one becomes empty after backspace
        if (index > 0) {
          inputRefs.current[index - 1]?.current?.focus();
        }
      } else if (index > 0) {
        // If current is already empty, backspace moves focus to previous and clears it (optional)
        inputRefs.current[index - 1]?.current?.focus();
         const newCodes = [...codes];
         newCodes[index-1] = '';
         setCodes(newCodes);
      }
    } else if (e.key === 'ArrowLeft' && index > 0) {
      inputRefs.current[index - 1]?.current?.focus();
    } else if (e.key === 'ArrowRight' && index < NUM_INPUTS - 1) {
      inputRefs.current[index + 1]?.current?.focus();
    }
  };

  const handleSubmit = () => {
    const fullCode = codes.join('');
    if (fullCode.length === NUM_INPUTS && !disabled) {
      onConfirm(fullCode);
    }
  };

  const isCodeComplete = codes.join('').length === NUM_INPUTS;

  return (
    <div className="space-y-2.5"> {/* Adjusted spacing slightly */}
      <p className="text-xxs text-gray-500 italic text-center sm:text-left"> {/* Centered on mobile, left on sm+ */}
        The buyer will provide a pickup code. Enter it below to confirm pickup and complete the order.
      </p>
      {/* Combined inputs and button into a single row, centered */}
      <div className="flex items-stretch justify-center space-x-1.5 sm:space-x-2">
        {codes.map((code, index) => (
          <input
            key={index}
            ref={inputRefs.current[index]}
            type="tel" // Use tel for numeric keyboard on mobile, but still allow text input for paste handling
            value={code}
            onChange={(e) => handleChange(index, e.target.value)}
            onKeyDown={(e) => handleKeyDown(index, e)}
            className="w-10 h-10 sm:w-12 sm:h-12 text-center border border-gray-300 rounded-md shadow-sm focus:border-brand-teal focus:ring-brand-teal text-lg sm:text-xl disabled:opacity-70 disabled:bg-gray-100" // Styling for square inputs
            maxLength={1} // Should be 1 per box, but paste logic handles multi-char paste into first box
            disabled={disabled || isLoading}
            autoComplete="off"
            pattern="[0-9]*" // Helps with mobile keyboard
            inputMode="numeric" // Helps with mobile keyboard
          />
        ))}
        {/* Button on the same row, items-stretch will match height */}
        <Button 
          onClick={handleSubmit}
          isLoading={isLoading}
          disabled={disabled || isLoading || !isCodeComplete}
          size="sm" // Relies on Button.js for padding of sm size
          // className removed, items-stretch from parent should handle height.
          // Width will be natural based on text and sm padding.
        >
          Confirm
        </Button>
      </div>
    </div>
  );
};

export default PickupCodeInput; 